<script setup lang="ts">
import { computed, provide } from 'vue';

import { useStore } from '@/store';
import { createAccessibleThemeTokens, type ThemeContrastInput } from '@/store/modules/brandKit/util';
import { toRgbChannels } from '@/utils/contrast';

type ThemeMode = 'user-configured' | 'fixed' | 'inherit';

const props = defineProps<{
  mode: ThemeMode;
  fixedTheme?: {
    primary: string;
    secondary: string;
    primaryText: string;
    secondaryText: string;
  };
}>();

const store = useStore();
const currentEvent = computed(() => store.state.event.event);
const toThemeContext = (theme: ThemeContrastInput) =>
  Object.fromEntries(
    Object.entries(createAccessibleThemeTokens(theme)).map(([token, color]) => [token, toRgbChannels(color)])
  );

const themeContext = computed(() => {
  if (props.mode === 'fixed') {
    const primary = store.state.event.goldcastThemeStyles['--primary-shade-1000'];
    const secondary = store.state.event.goldcastThemeStyles['--primaryText-shade-50'];
    return toThemeContext({
      primaryBackground: primary,
      secondaryBackground: secondary,
      primaryForeground: secondary,
      secondaryForeground: primary
    });
  }

  if (props.mode === 'user-configured') {
    const theme = currentEvent.value?.theme_obj || {};
    return toThemeContext({
      primaryBackground: theme.theme_primary_color,
      secondaryBackground: theme.theme_secondary_color,
      primaryForeground: theme.primary_text_color,
      secondaryForeground: theme.secondary_text_color
    });
  }

  // inherit mode - use parent theme or default
  return {};
});

provide('theme-context', themeContext.value);
</script>

<template>
  <div :class="mode !== 'inherit' && 'theme-primary-surface'" :style="themeContext">
    <slot />
  </div>
</template>
